import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';

const AdminUtilitasUbahStatusPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [user, setUser] = useState(null);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false,
    utilitasOpen: true
  });
  
  const [selectedModule, setSelectedModule] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const modules = [
    { value: 'kts', label: 'Laporan KTS', endpoint: '/api/admin/kts-reports/bulk-reject' },
    { value: 'lembur', label: 'Laporan Lembur', endpoint: '/api/admin/laporan-lembur/bulk-reject' },
    { value: 'konseling', label: 'Konseling', endpoint: '/api/admin/konseling/bulk-reject' },
    { value: 'ijin-interupsi', label: 'Surat Ijin Interupsi', endpoint: '/api/admin/ijin-interupsi/bulk-reject' },
    { value: 'ijin-tidakmmasuk', label: 'Surat Pemberitahuan Tidak Masuk', endpoint: '/api/admin/ijin-tidakmasuk/bulk-reject' },
    { value: 'kersos', label: 'Laporan Kersos', endpoint: '/api/admin/laporan-kersos/bulk-reject' },
    { value: 'psm', label: 'Laporan PSM', endpoint: '/api/admin/laporan-psm/bulk-reject' },
    { value: 'surat-peringatan', label: 'Surat Peringatan', endpoint: '/api/admin/surat-peringatan/bulk-reject' }
  ];

  // Check admin access
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'admin_user') {
      navigate('/login');
      return;
    }
    
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      }
    }
  }, [navigate]);

  const handleProcess = async () => {
    if (!selectedModule) {
      setNotification({
        show: true,
        message: 'Silakan pilih modul terlebih dahulu',
        type: 'error'
      });
      return;
    }

    if (!startDate || !endDate) {
      setNotification({
        show: true,
        message: 'Silakan pilih tanggal mulai dan tanggal selesai',
        type: 'error'
      });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setNotification({
        show: true,
        message: 'Tanggal mulai tidak boleh lebih besar dari tanggal selesai',
        type: 'error'
      });
      return;
    }

    const confirmMessage = `Apakah Anda yakin ingin mengubah semua status menjadi "Ditolak" untuk modul "${modules.find(m => m.value === selectedModule)?.label}" dari tanggal ${startDate} sampai ${endDate}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      
      const module = modules.find(m => m.value === selectedModule);
      
      // Add time to dates: start date at 00:00:00, end date at 23:59:59
      const startDateTime = `${startDate} 00:00:00`;
      const endDateTime = `${endDate} 23:59:59`;
      
      const response = await authFetch(module.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: startDateTime,
          end_date: endDateTime
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setNotification({
          show: true,
          message: `Berhasil mengubah ${result.updated_count || 0} data menjadi ditolak`,
          type: 'success'
        });
        
        // Reset form
        setSelectedModule('');
        setStartDate('');
        setEndDate('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Gagal mengubah status');
      }
    } catch (error) {
      console.error('Error processing bulk reject:', error);
      setNotification({
        show: true,
        message: `Gagal mengubah status: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Utilitas - Ubah Status Ditolak</h1>
            <Subtitle>Ubah status data secara massal menjadi ditolak</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <FormContainer>
              <InfoBox>
                <InfoIcon>ℹ️</InfoIcon>
                <InfoText>
                  Fitur ini akan mengubah semua data dengan status "Menunggu" menjadi "Ditolak" 
                  untuk modul dan rentang tanggal yang dipilih. Data yang sudah "Disetujui" tidak akan diubah.
                </InfoText>
              </InfoBox>

              <FormGroup>
                <FormLabel>Pilih Modul</FormLabel>
                <FormSelect 
                  value={selectedModule} 
                  onChange={(e) => setSelectedModule(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Pilih Modul --</option>
                  {modules.map(module => (
                    <option key={module.value} value={module.value}>
                      {module.label}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>

              <DateRangeContainer>
                <FormGroup>
                  <FormLabel>Tanggal Mulai</FormLabel>
                  <FormInput 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={loading}
                  />
                  <FormHint>Waktu akan diset ke 00:00:00</FormHint>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Tanggal Selesai</FormLabel>
                  <FormInput 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={loading}
                  />
                  <FormHint>Waktu akan diset ke 23:59:59</FormHint>
                </FormGroup>
              </DateRangeContainer>

              <WarningBox>
                <WarningIcon>⚠️</WarningIcon>
                <WarningText>
                  Perhatian: Aksi ini tidak dapat dibatalkan. Pastikan Anda telah memilih modul dan rentang tanggal yang benar.
                </WarningText>
              </WarningBox>

              <ButtonContainer>
                <ProcessButton 
                  onClick={handleProcess} 
                  disabled={loading || !selectedModule || !startDate || !endDate}
                >
                  {loading ? 'Memproses...' : 'Proses Ubah Status'}
                </ProcessButton>
              </ButtonContainer>
            </FormContainer>
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  padding: 2rem;
`;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const InfoBox = styled.div`
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const InfoText = styled.div`
  font-size: 0.875rem;
  color: #1565c0;
  line-height: 1.5;
`;

const WarningBox = styled.div`
  background-color: #fff3e0;
  border-left: 4px solid #ff9800;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const WarningIcon = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const WarningText = styled.div`
  font-size: 0.875rem;
  color: #e65100;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.938rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background-color: var(--background-light);
    cursor: not-allowed;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.938rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background-color: var(--background-light);
    cursor: not-allowed;
  }
`;

const FormHint = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  margin-top: 0.25rem;
`;

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ProcessButton = styled.button`
  background-color: var(--danger, #c01c28);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #a01622;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(192, 28, 40, 0.3);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  background-color: ${props => props.$type === 'success' ? '#e6f8f5' : '#ffe6e6'};
  color: ${props => props.$type === 'success' ? '#2ec27e' : '#c01c28'};
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  min-width: 300px;
  max-width: 500px;
`;

const NotificationMessage = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
  margin-left: 1rem;
  padding: 0;
  line-height: 1;
`;

export default AdminUtilitasUbahStatusPage;
